import { ContractStage, isWritingStage } from '../../types/contract';
import './ContractStageStepper.css';

interface ContractStageStepperProps {
  stages: ContractStage[];
  /** 계약의 실제 진행 단계 */
  currentStageId?: number | null;
  currentStageName?: string | null;
  /** 지금 보고 있는 단계 (보기만) */
  activeIndex: number;
  onSelect: (index: number) => void;
  /** 다음 단계로 진행 */
  onAdvance?: () => void;
  /** 이전 단계로 되돌리기 */
  onRetreat?: () => void;
  advancing?: boolean;
  onEditStages?: () => void;
  showEditButton?: boolean;
  disabled?: boolean;
  /** 진행 변경 가능 여부 (상세에서 권한 없을 때 false) */
  canAdvance?: boolean;
}

export function resolveCurrentStageIndex(
  stages: ContractStage[],
  currentStageId?: number | null,
  currentStageName?: string | null
) {
  const byId = stages.findIndex(
    (stage) => currentStageId != null && stage.stageId === currentStageId
  );
  if (byId >= 0) return byId;

  const byName = stages.findIndex(
    (stage) => !!currentStageName && stage.stageName === currentStageName
  );
  if (byName >= 0) return byName;

  const writing = stages.findIndex((stage) => isWritingStage(stage));
  return writing >= 0 ? writing : 0;
}

function ContractStageStepper({
  stages,
  currentStageId,
  currentStageName,
  activeIndex,
  onSelect,
  onAdvance,
  onRetreat,
  advancing = false,
  onEditStages,
  showEditButton = false,
  disabled = false,
  canAdvance = true,
}: ContractStageStepperProps) {
  const currentIndex = resolveCurrentStageIndex(stages, currentStageId, currentStageName);
  const currentName = stages[currentIndex]?.stageName || currentStageName || '작성';
  const nextStage = stages[currentIndex + 1] ?? null;
  const prevStage = stages[currentIndex - 1] ?? null;
  const viewingOther = activeIndex !== currentIndex;
  const viewingName = stages[activeIndex]?.stageName;
  const progressPct =
    stages.length <= 1 ? 100 : Math.round((currentIndex / (stages.length - 1)) * 100);

  return (
    <div className="contract-stage-stepper">
      <div className="contract-stage-stepper__meta">
        <div className="contract-stage-stepper__status">
          <span className="contract-stage-stepper__status-label">현재 진행</span>
          <strong className="contract-stage-stepper__status-name">{currentName}</strong>
          <span className="contract-stage-stepper__status-count">
            {currentIndex + 1} / {stages.length} 단계
          </span>
          {viewingOther && viewingName ? (
            <span className="contract-stage-stepper__viewing-hint">
              보는 중: {viewingName}
            </span>
          ) : null}
        </div>
        <div className="contract-stage-stepper__actions">
          {showEditButton && onEditStages ? (
            <button type="button" className="contract-stage-stepper__edit" onClick={onEditStages}>
              단계 편집
            </button>
          ) : null}
          {canAdvance && onRetreat && prevStage ? (
            <button
              type="button"
              className="contract-stage-stepper__retreat"
              disabled={disabled || advancing}
              onClick={onRetreat}
            >
              ← 이전 단계
            </button>
          ) : null}
          {canAdvance && onAdvance ? (
            <button
              type="button"
              className="contract-stage-stepper__advance"
              disabled={disabled || advancing || !nextStage}
              onClick={onAdvance}
              title={nextStage ? `다음: ${nextStage.stageName}` : '마지막 단계입니다'}
            >
              {advancing
                ? '진행 중...'
                : nextStage
                  ? `다음 단계 진행 · ${nextStage.stageName}`
                  : '최종 단계'}
            </button>
          ) : null}
        </div>
      </div>

      <p className="contract-stage-stepper__help">
        단계를 클릭하면 내용만 확인합니다. 진행 상태는 <strong>다음 단계 진행</strong>으로
        바꿉니다.
      </p>

      <div className="contract-stage-stepper__track" role="list" aria-label="계약 진행 단계">
        <div className="contract-stage-stepper__rail" aria-hidden="true">
          <div
            className="contract-stage-stepper__rail-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="contract-stage-stepper__row">
          {stages.map((stage, index) => {
            const isViewing = index === activeIndex;
            const isCurrent = index === currentIndex;
            const isDone = index < currentIndex;
            const state = isCurrent ? 'current' : isDone ? 'done' : 'todo';

            return (
              <button
                key={`${stage.stageKey}-${stage.stageName}-${index}`}
                type="button"
                role="listitem"
                className={[
                  'contract-stage-stepper__step',
                  `is-${state}`,
                  isViewing ? 'is-viewing' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                disabled={disabled}
                onClick={() => onSelect(index)}
                title={`${stage.stageName} 보기`}
              >
                <span className="contract-stage-stepper__node" aria-hidden="true">
                  {isDone ? '✓' : index + 1}
                </span>
                <span className="contract-stage-stepper__label">
                  <span className="contract-stage-stepper__name">{stage.stageName}</span>
                  {isCurrent ? (
                    <span className="contract-stage-stepper__badge">진행 중</span>
                  ) : isDone ? (
                    <span className="contract-stage-stepper__badge is-done">완료</span>
                  ) : (
                    <span className="contract-stage-stepper__badge is-todo">대기</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ContractStageStepper;
